/**
 * Core Types for EHR Trends Feature
 *
 * This file defines all interfaces used across the Trends feature.
 * These types are the foundation for TrendDataProvider, APIs, and UI components.
 *
 * @see docs/trends/architecture.md for full architecture documentation
 */

// -----------------------------------------------------------------------------
// Query & Response Types
// -----------------------------------------------------------------------------

/** Query parameters for fetching trend data */
export interface BiomarkerLabelMaps {
  /** Per-practice LOINC → display label overrides (ConceptMap-backed during the rollout). */
  practice: Record<string, string>;
  /** Global canonical LOINC → display labels (ConceptMap-backed super-org map). */
  canonical: Record<string, string>;
}

export interface TrendQuery {
  /** Patient ID to fetch data for */
  patientId: string;
  /** LOINC code for the biomarker (e.g., "80404-7" for HRV) */
  loincCode: string;
  /** Date range for the query */
  dateRange: {
    start: string; // ISO date
    end: string;
  };
  /** Aggregation period */
  aggregation?: "day" | "week" | "month";
  /** Filter by device provider (e.g., "oura", "apple_health_kit") */
  provider?: string;
  /** Optional IANA timezone used to bucket timestamped observations by local day. */
  timezone?: string;
  /**
   * Practice `Organization` id when viewing from provider EHR — enables per-practice biomarker
   * display name overrides. Omitted for patient self-view or when maps are not needed.
   */
  practiceId?: string;
  /**
   * Optional pre-loaded label maps (e.g. batch `getAllTrendData` avoids repeated label-map reads).
   * When set, `practiceId` should still be present for resolution semantics.
   */
  labelMaps?: BiomarkerLabelMaps;
}

/** Normalized trend data returned by TrendDataProvider */
export interface TrendData {
  /** Array of data points (deduplicated — one per day across all sources) */
  observations: TrendDataPoint[];
  /**
   * Raw observations grouped by providerSlug, before cross-source deduplication.
   * Used by the Vitals view to render one card per source (e.g. "Chart" vs "Oura Ring").
   * Keys match TrendDataPoint.provider values (e.g. "clinic", "oura").
   */
  bySource?: Record<string, TrendDataPoint[]>;
  /** Human-readable rollup of all origins represented in this trend response. */
  originSummary?: TrendOriginSummary;
  /** Computed statistics for the data */
  statistics: TrendStatistics;
  /** Standard reference range for the biomarker */
  referenceRange?: ReferenceRange;
  /**
   * Human-readable reference range when the source only provides a one-sided or textual bound
   * (for example, "<29" from a lab report).
   */
  referenceRangeLabel?: string;
  /** Personalized goal range (from FHIR Goal if set) */
  goalRange?: ReferenceRange;
  /** Metadata about the data source */
  metadata: TrendMetadata;
}

/** A single data point in a trend */
export interface TrendDataPoint {
  /** ISO date (YYYY-MM-DD) */
  date: string;
  /** The measured value */
  value: number;
  /** Raw lab text when the source reported a one-sided bound (for example, "<10"). */
  displayValue?: string;
  /** Device source (e.g., "oura", "apple_health_kit") */
  provider?: string;
  /** Source attribution for this value. */
  origin?: TrendDataOrigin;
  /** Source rollup when this value aggregates multiple underlying observations. */
  originSummary?: TrendOriginSummary;
  /** Whether this value is outside the optimal range */
  isOutOfRange?: boolean;
  /**
   * When the value was unit-converted at read time, the original reading in its source unit —
   * surfaced for provenance (e.g. "Converted from: 5.6 mmol/L") when the source unit differs.
   */
  convertedFrom?: { value: number; unit: string };
}

/** Source type used for Trends accountability UI. */
export type TrendDataOriginKind = "wearable" | "document" | "clinic" | "synthetic" | "unknown";

/** Reference to the upstream resource that produced a trend observation. */
export interface TrendOriginReference {
  /** FHIR reference string such as "DocumentReference/abc" or "Observation/note-id". */
  reference: string;
  /** Human-readable label when available. */
  label?: string;
}

/** Source attribution for a single trend data point. */
export interface TrendDataOrigin {
  /** Classification of the observation origin. */
  kind: TrendDataOriginKind;
  /** Compact label suitable for table and chart tooltips. */
  label: string;
  /** Wearable or clinic provider slug when applicable. */
  providerSlug?: string;
  /** Origin source subtype when supplied by Junction/Vital (e.g., watch, ring). */
  sourceType?: string;
  /** Source resources used to trace this observation back to its origin. */
  references?: TrendOriginReference[];
  /** Note ID for note-editor Vitals when available. */
  noteId?: string;
  /** Note editor node ID for clinic Vitals when available. */
  noteNodeId?: string;
  /** StagingReviewSet id to open in the review flyover — parsed-document provenance (demo). */
  reviewSetId?: string;
  /** Source document label for the provenance hover. */
  sourceLabel?: string;
  /** Provenance state code (verified/high/medium/low/inferred) — the hover's confidence label. */
  provenanceState?: string;
  /** StagedValue id within the review set — focuses that row when the flyover opens. */
  provenanceValueId?: string;
}

/** Human-readable rollup for rows that include one or more origins. */
export interface TrendOriginSummary {
  /** True when multiple distinct source labels contributed to the row. */
  hasMultipleSources: boolean;
  /** Number of distinct source labels represented in the row. */
  sourceCount: number;
  /** Ordered display labels for all sources represented in the row. */
  labels: string[];
  /** Tooltip-ready label such as "Source: Oura Ring" or "Multiple sources: Chart, Oura Ring". */
  tooltipLabel: string;
}

/** Computed statistics for trend data */
export interface TrendStatistics {
  /** Most recent value */
  current: number;
  /** Comparator-preserving display for `current` (e.g. "<3"), when the latest reading is censored. */
  currentDisplayValue?: string;
  /** Average value in the period */
  average: number;
  /** Minimum value in the period */
  min: number;
  /** Maximum value in the period */
  max: number;
  /** Number of data points */
  count: number;
  /** Rolling 30-day average (optional) */
  rolling30DayAvg?: number;
  /** Percent change vs previous period */
  percentChange?: number;
  /** Overall trend direction */
  trend: "increasing" | "decreasing" | "stable";
  /** Previous values for dashboard table "Previous" columns */
  previousValues?: Array<{ date: string; value: number; displayValue?: string }>;
}

/** Metadata about the data source */
export interface TrendMetadata {
  /** Where the data came from */
  source: "fhir" | "cq_cache" | "hybrid" | "mock";
  /** When the data was last updated */
  lastUpdated: string;
  /** Unit of measure */
  unit: string;
  /** Human-readable name */
  displayName: string;
  /** Longer human-readable name for detail views and hover context. */
  detailDisplayName?: string;
  /** Original source/LOINC display name preserved for clinical traceability. */
  sourceDisplayName?: string;
  /**
   * Distinct FHIR Observation `category` codes (e.g. "laboratory", "activity",
   * "vital-signs") across the underlying observations. Used to route rows to the
   * Continuous Data section vs. the discrete Biomarkers table when the biomarker
   * config lacks a `supportsCQ` match (e.g. runtime-discovered wearable rows).
   */
  observationCategories?: readonly string[];
  /**
   * Error reason if the data fetch failed.
   * Present only when a query error occurred (e.g., FHIR query failed).
   * Absent for legitimate empty results (e.g., patient has no observations).
   * Clients can check `if (metadata.error)` to distinguish fetch failures.
   */
  error?: string;
}

// -----------------------------------------------------------------------------
// Reference & Goal Ranges
// -----------------------------------------------------------------------------

/** Reference range for a biomarker */
export interface ReferenceRange {
  /** Normal range (acceptable) */
  normal: { min: number; max: number };
  /** Optimal range (ideal) */
  optimal: { min: number; max: number };
}

/** Personalized goal set by provider */
export interface PersonalizedGoal {
  /** Minimum target value */
  min: number;
  /** Maximum target value */
  max: number;
  /** Provider who set the goal */
  setBy: string;
  /** When the goal was set (ISO date) */
  setAt: string;
}

// -----------------------------------------------------------------------------
// Glucose-Specific Metrics
// -----------------------------------------------------------------------------

/**
 * Glucose-specific computed metrics for CGM cards.
 * These are specialized metrics for continuous glucose monitoring.
 */
export interface GlucoseMetrics {
  /** Average glucose level (mg/dL) */
  averageGlucose: number;
  /** Percentage (0-100) of time in target range (typically 70-180 mg/dL) */
  timeInRange: number;
  /** Coefficient of variation (CV%) - measure of glucose stability */
  glucoseVariability: number;
}

// -----------------------------------------------------------------------------
// AI & Insights Types
// -----------------------------------------------------------------------------

/** AI-generated chart insights */
export interface ChartInsights {
  /** Summary of the overall trend */
  summary: string;
  /** Key observations as bullet points */
  bullets: InsightBullet[];
  /** Optional suggested action */
  suggestedAction?: string;
}

/** A single insight bullet point */
export interface InsightBullet {
  /** The insight text */
  text: string;
  /** Severity/type of the insight */
  type: "warning" | "info" | "success";
}

/** A trend insight (used in dashboard) */
export interface TrendInsight {
  /** Type of insight */
  type: "warning" | "info" | "success";
  /** Title of the insight */
  title: string;
  /** Description text */
  description: string;
  /** Associated biomarker (if any) */
  biomarkerCode?: string;
}

/**
 * AI-generated insight for a functional category.
 * Used in the category dropdown to show summaries like
 * "Mostly optimized with early insulin sensitivity drift".
 */
export interface CategoryInsight {
  /** The functional category */
  category: FunctionalSystem;
  /** AI-generated summary of the category's health status */
  summary: string;
  /** Overall severity/status of the category */
  severity: "good" | "warning" | "critical";
}

/** Cross-biomarker insight (e.g. "Normal Mean Glucose + Rising Variability + Rising Insulin = early loss of metabolic flexibility") */
export interface CrossBiomarkerInsight {
  /** Short title combining biomarkers (e.g. "Normal Mean Glucose + Rising Variability + Rising Insulin") */
  title: string;
  /** Clinical description / reasoning */
  description: string;
  /** Severity */
  severity: "info" | "warning" | "critical";
  /** LOINC codes of related biomarkers */
  relatedBiomarkers: string[];
}

/** Result of cross-biomarker insights generation; includes per-biomarker one-liners for B6. */
export interface CrossBiomarkerInsightsResult {
  insights: CrossBiomarkerInsight[];
  /** LOINC code -> one-line summary for each biomarker */
  biomarkerSummaries: Record<string, string>;
}

/** Delta for a biomarker between encounter date and most recent value (Changes Since Last Encounter). */
export interface BiomarkerDelta {
  loincCode: string;
  displayName: string;
  unit: string;
  previousValue: number;
  previousDate: string;
  currentValue: number;
  currentDate: string;
  delta: number;
  percentChange: number;
  direction: "up" | "down" | "stable";
  isNewlyOutOfRange: boolean;
}

/** An anomaly detected in trend data */
export interface Anomaly {
  /** Date of the anomaly */
  date: string;
  /** The anomalous value */
  value: number;
  /** Type of anomaly */
  type: "spike" | "drop";
  /** Human-readable description */
  description: string;
  /** How many standard deviations from mean */
  zScore: number;
}

// -----------------------------------------------------------------------------
// Intervention Markers
// -----------------------------------------------------------------------------

/** A marker for an intervention (protocol, medication, supplement start) */
export interface InterventionMarker {
  /** Date of the intervention */
  date: string;
  /** Type of intervention */
  type: "protocol_start" | "medication_start" | "supplement_start";
  /** Label to display */
  label: string;
  /** Color for the marker */
  color: string;
}

// -----------------------------------------------------------------------------
// Configuration Types
// -----------------------------------------------------------------------------

/**
 * Functional system categories for grouping biomarkers.
 * The first 9 match the Figma design; "other" is a hidden bucket for
 * dynamically discovered lab biomarkers not in the curated config.
 *
 * Note: "other" is intentionally excluded from TREND_CATEGORIES (constants.ts)
 * so it doesn't appear in the UI category picker.
 *
 * @see https://www.figma.com/design/pqY81U2nOTtJeHfVVHpk2Q/EHR-Trends-Exploration?node-id=2650-3328
 */
export type FunctionalSystem =
  | "metabolism"
  | "cardiovascular"
  | "autonomic"
  | "inflammation"
  | "endocrine"
  | "muscular_skeletal"
  | "sleep"
  | "nutrients"
  | "digestive"
  | "other";

/**
 * Lab test / panel grouping for curated lab biomarkers.
 * Used by the "By Test / Panel" sort mode to cluster a biomarker under the
 * lab panel it is most commonly ordered within (clinical membership, not
 * functional system). Curated-only: a biomarker that doesn't clearly belong
 * to one of these panels leaves `panel` undefined and sorts last ("Other").
 */
export type LabPanel =
  | "lipid"
  | "cbc"
  | "bmp"
  | "renal"
  | "liver"
  | "thyroid"
  | "glycemic"
  | "iron"
  | "vitamins"
  | "inflammation"
  | "hormones"
  | "cardiac"
  | "bone"
  | "muscle"
  | "pancreatic"
  | "autoimmune"
  | "infectious"
  | "toxicology";

/** Biomarker data frequency classification */
export type BiomarkerDataType = "continuous" | "discrete" | "all";

/** Options for fetching biomarker inventories */
export interface AllBiomarkersOptions {
  /** Optional date range to limit discovery */
  dateRange?: { start: string; end: string };
  /** Filter by data type */
  dataType?: BiomarkerDataType;
  /** Filter by functional category */
  category?: FunctionalSystem;
  /** Practice scope for patient web requests */
  practiceId?: string;
}

/** Supported chart types for biomarker detail views */
export type BiomarkerChartType = "line" | "bar" | "circle";

/**
 * Options controlling what overlays are visible on sparkline charts.
 * Supplied by chart callers and consumed by hero cards and BiomarkerTable.
 */
export interface ChartOptions {
  /** Show the blue shaded normal range area on charts. */
  showNormalRange: boolean;
  /** Show the optimal range dashed marker lines on charts. */
  showOptimalRange: boolean;
  /** Show the max/min data-range reference lines on charts. */
  showChangeMaxMin: boolean;
}

/** Available time range values for continuous monitoring cards. "max" means show all data with no cutoff. */
export type TimeRangeValue = "1d" | "1w" | "2w" | "1m" | "1y" | "2y" | "5y" | "max";

/** Configuration for a biomarker */
export interface BiomarkerConfig {
  /** LOINC code */
  loincCode: string;
  /** Human-readable display name */
  displayName: string;
  /** Longer human-readable display name for detail views and hover context */
  detailDisplayName?: string;
  /** Original source/LOINC display name preserved for clinical traceability */
  sourceDisplayName?: string;
  /** Short name for compact displays */
  shortName: string;
  /** Unit of measure */
  unit: string;
  /** Functional system category */
  category: FunctionalSystem;
  /**
   * Lab test / panel this biomarker is clinically ordered within (curated).
   * Drives the "By Test / Panel" sort mode. Only set for clear panel members;
   * left undefined for labs that don't map cleanly and for non-lab metrics.
   */
  panel?: LabPanel;
  /** Associated model class name (null for labs) */
  modelClass: string | null;
  /** Reference range (null for context-dependent metrics like general HR) */
  referenceRange: ReferenceRange | null;
  /** Whether Junction CQ is available for this metric */
  supportsCQ: boolean;
  /** Whether this is a lab result (vs wearable) */
  isLabResult?: boolean;
  /** Chart type for the detail view (defaults to "line" if unset) */
  chartType?: BiomarkerChartType;
  /**
   * Text search terms for matching Observations that lack LOINC codes.
   * Used as fallback when `code=<LOINC>` returns empty — searches `code:text`
   * against `Observation.code.text` and `Observation.code.coding[].display`.
   *
   * Needed because PDF-parsed lab results often have only `code.text` (e.g. "TSH")
   * without LOINC coding. Terms are tried in order; first match wins.
   */
  textSearchTerms?: string[];
  /**
   * Alternate stored LOINC codes that should hydrate the same curated biomarker.
   * Used before any legacy text fallback so equivalent serum/plasma variants
   * merge into one stable trends card.
   */
  equivalentLoincCodes?: string[];
}

// -----------------------------------------------------------------------------
// Correlation & Forecasting Types
// -----------------------------------------------------------------------------

/** Correlation between two metrics */
export interface Correlation {
  /** First metric LOINC code */
  metric1: string;
  /** Second metric LOINC code */
  metric2: string;
  /** Correlation coefficient (-1 to 1) */
  coefficient: number;
  /** Strength classification */
  strength: "strong" | "moderate" | "weak";
  /** Direction of correlation */
  direction: "positive" | "negative";
  /** LLM-generated explanation (optional) */
  explanation?: string;
}

/** A single forecast data point */
export interface ForecastPoint {
  /** Predicted date */
  date: string;
  /** Predicted value */
  predictedValue: number;
  /** Lower confidence bound */
  confidenceLow: number;
  /** Upper confidence bound */
  confidenceHigh: number;
}

/** Forecast result for a biomarker */
export interface Forecast {
  /** LOINC code */
  loincCode: string;
  /** Array of predictions */
  predictions: ForecastPoint[];
  /** Model used for prediction */
  model: string;
  /** Backtesting accuracy (optional) */
  accuracy?: number;
}

// -----------------------------------------------------------------------------
// Natural Language Query Types
// -----------------------------------------------------------------------------

/** Result from a natural language query */
export interface NLQueryResult {
  /** How the query was interpreted */
  interpretation: string;
  /** The parsed structured query (null if couldn't parse) */
  query: TrendQuery | null;
  /** The fetched data (null if couldn't parse) */
  data: TrendData | null;
  /** LLM-generated answer */
  answer: string;
  /** Confidence score */
  confidence?: number;
}
